/**
 * Frontend error reporter
 * Catches unhandled errors and promise rejections, sends them to /api/client-error.
 * Batched and deduplicated to avoid spamming the server.
 */

const REPORT_ENDPOINT = '/api/client-error';
const BATCH_MS = 5000;
const MAX_ERRORS_PER_BATCH = 10;

let queue = [];
let flushTimer = null;

function flushErrors() {
    if (queue.length === 0) return;
    const batch = queue.slice(0, MAX_ERRORS_PER_BATCH);
    queue = queue.slice(MAX_ERRORS_PER_BATCH);

    batch.forEach(err => {
        try {
            if (navigator.sendBeacon) {
                // Blob с application/json: sendBeacon со строкой шлёт text/plain,
                // который express.json() не парсит (источник «пустых» ошибок)
                navigator.sendBeacon(REPORT_ENDPOINT, new Blob([JSON.stringify(err)], { type: 'application/json' }));
            } else {
                fetch(REPORT_ENDPOINT, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify(err),
                    keepalive: true
                }).catch(() => {});
            }
        } catch (e) {
            // Silent: we must not throw from the error handler.
        }
    });

    if (queue.length > 0) {
        flushTimer = setTimeout(flushErrors, BATCH_MS);
    } else {
        flushTimer = null;
    }
}

// Известный экологический шум: не засоряет ленту (обрабатывается нами)
const NOISE_RE = /^newestWorker is null|^Failed to register a ServiceWorker|.*ServiceWorker.*fetching the script/;

const seen = new Map(); // key -> count, дедупликация одинаковых ошибок в сессии
function dedupeKey(e) { return (e.message||'') + '|' + (e.source||'') + ':' + e.line; }

function enqueue(errorInfo) {
    const msg = String(errorInfo.message || '');
    if (NOISE_RE.test(msg)) return;
    const k = dedupeKey(errorInfo);
    const n = (seen.get(k) || 0) + 1;
    seen.set(k, n);
    if (n > 3) return; // максимум 3 повтора одной ошибки за сессию
    queue.push(errorInfo);
    if (!flushTimer) {
        flushTimer = setTimeout(flushErrors, BATCH_MS);
    }
}

function makePayload(error, extra = {}) {
    return {
        message: error && error.message ? error.message : String(error),
        stack: error && error.stack ? error.stack : '',
        url: typeof window !== 'undefined' ? window.location.href : '',
        source: extra.source || '',
        line: extra.lineno || 0,
        column: extra.colno || 0,
        ...extra
    };
}

function initErrorReporter() {
    if (typeof window === 'undefined') return;

    window.addEventListener('error', event => {
        enqueue(makePayload(event.error || event.message, {
            source: event.filename,
            lineno: event.lineno,
            colno: event.colno
        }));
    }, true);

    window.addEventListener('unhandledrejection', event => {
        const reason = event.reason;
        let error;
        if (reason instanceof Error) {
            error = reason;
        } else if (reason === undefined || reason === null) {
            // раньше терялось как "undefined": фиксируем хоть что-то полезное
            error = new Error('Unhandled rejection: ' + (reason === null ? 'null' : 'undefined'));
            error.stack = '(no stack) reason=' + String(reason);
        } else if (typeof reason === 'object') {
            let json; try { json = JSON.stringify(reason).slice(0, 500); } catch (e) { json = '[unserializable]'; }
            error = new Error('Unhandled rejection object: ' + json);
        } else {
            error = new Error('Unhandled rejection: ' + String(reason).slice(0, 300));
        }
        enqueue(makePayload(error, { context: 'unhandledrejection' }));
    });

    window.addEventListener('app-error', event => {
        const detail = event.detail || {};
        const error = detail.error instanceof Error ? detail.error : new Error(String(detail.error || 'app-error'));
        enqueue(makePayload(error, { context: detail.context || 'app-error' }));
    });
}

initErrorReporter();

export { initErrorReporter };
